import { buildHostedTrpcUrl, normalizeHostedBackendBaseUrl } from '@owlbert/core'

/** When no override is set, browser tRPC targets production (`…/trpc` via `@owlbert/core`). */
export const DEFAULT_OWLBERT_BACKEND_URL = 'https://api.owlbert.ai'

export type HostedBackendUrlSource =
  | 'runtime_inject'
  | 'vite_env'
  | 'vite_api_url'
  | 'same_origin_proxy'
  | 'default'

declare global {
  interface Window {
    __OWLBERT_BACKEND_URL__?: string
    /** Set by owlbert-web BFF when `/trpc` is reverse-proxied to owlbert-backend. */
    __OWLBERT_TRPC_PROXY__?: boolean
  }
}

function isLocalDevPageOrigin(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  if (host !== 'localhost' && host !== '127.0.0.1' && host !== '::1') return false
  const port = window.location.port
  // owlbert-web BFF (3847) and Vite UI (5173) reverse-proxy `/trpc` in local dev.
  return port === '3847' || port === '5173' || port === ''
}

function shouldUseSameOriginTrpcProxy(): boolean {
  if (import.meta.env.VITE_OWLBERT_BACKEND_DIRECT === '1') return false
  if (typeof window !== 'undefined') {
    if (window.__OWLBERT_TRPC_PROXY__ === true) return true
    if (isLocalDevPageOrigin()) return true
  }
  // Vite dev server proxies `/trpc` — never call a cross-origin backend URL from the browser.
  return import.meta.env.DEV
}

/** Raw value from Vite inlining (before `@owlbert/core` normalization). */
export function readRawHostedBackendEnvOverride(): string | undefined {
  if (shouldUseSameOriginTrpcProxy()) return undefined
  return import.meta.env.VITE_OWLBERT_BACKEND_URL?.trim() || import.meta.env.VITE_API_URL?.trim() || undefined
}

export type ResolvedHostedBackendUrl = {
  /** API origin (no trailing `/trpc`). */
  url: string
  /** Full tRPC HTTP adapter URL. */
  trpcUrl: string
  source: HostedBackendUrlSource
}

/**
 * API origin or full `…/trpc` URL (normalized by `@owlbert/core`).
 * Local dev: same-origin `/trpc` (Vite or BFF proxy). Production: runtime inject → Vite env → default.
 */
export function resolveHostedBackendUrl(): ResolvedHostedBackendUrl {
  if (typeof window !== 'undefined' && shouldUseSameOriginTrpcProxy()) {
    const url = window.location.origin
    return { url, trpcUrl: buildHostedTrpcUrl(url), source: 'same_origin_proxy' }
  }

  if (typeof window !== 'undefined') {
    const injected = window.__OWLBERT_BACKEND_URL__?.trim()
    if (injected) {
      const url = normalizeHostedBackendBaseUrl(injected)
      return { url, trpcUrl: buildHostedTrpcUrl(url), source: 'runtime_inject' }
    }
  }
  const primary = import.meta.env.VITE_OWLBERT_BACKEND_URL?.trim()
  if (primary) {
    const url = normalizeHostedBackendBaseUrl(primary)
    return { url, trpcUrl: buildHostedTrpcUrl(url), source: 'vite_env' }
  }
  const apiUrl = import.meta.env.VITE_API_URL?.trim()
  if (apiUrl) {
    const url = normalizeHostedBackendBaseUrl(apiUrl)
    return { url, trpcUrl: buildHostedTrpcUrl(url), source: 'vite_api_url' }
  }
  const url = DEFAULT_OWLBERT_BACKEND_URL
  return { url, trpcUrl: buildHostedTrpcUrl(url), source: 'default' }
}

/** One-shot startup log (browser console). Always printed so production overrides are visible. */
export function logHostedBackendUrlOnStartup(): void {
  const raw = readRawHostedBackendEnvOverride()
  const { url, trpcUrl, source } = resolveHostedBackendUrl()
  const detail =
    raw && raw !== url ? ` (env raw: ${raw})` : raw ? ` (env: ${raw})` : ''
  // eslint-disable-next-line no-console
  console.log(`[OwlbertWeb] Hosted backend: ${url} → tRPC: ${trpcUrl} (source: ${source})${detail}`)
}
